import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { DocumentScanner } from '@capgo/capacitor-document-scanner';

import { logger } from 'utils/logger';

import {
  ANDROID_PICTURES_SUBDIR,
  CAPTURED_FILE_PREFIX,
  DEFAULT_CAPTURE_MIME,
  DEFAULT_CAPTURE_QUALITY,
} from './nativeCamera.constants';

const getExtensionFromFormat = format => (format ? `.${format}` : '.jpeg');

const buildFileFromBlob = (blob, format) => {
  const fileName = `${CAPTURED_FILE_PREFIX}${Date.now()}${getExtensionFromFormat(format)}`;

  return new File([blob], fileName, { type: blob.type || DEFAULT_CAPTURE_MIME });
};

export const captureDocumentPhoto = async () => {
  try {
    const photo = await Camera.getPhoto({
      source: CameraSource.Camera,
      resultType: CameraResultType.Uri,
      quality: DEFAULT_CAPTURE_QUALITY,
      correctOrientation: true,
      saveToGallery: false,
    });

    if (!photo?.webPath) {
      return null;
    }

    const response = await fetch(photo.webPath);
    const blob = await response.blob();

    return buildFileFromBlob(blob, photo.format);
  } catch (error) {
    logger.error('[NativeCamera] capture failed', error);

    return null;
  }
};

const base64ToBlob = (base64, mime) =>
  new Blob([Uint8Array.from(atob(base64), char => char.charCodeAt(0))], { type: mime });

/**
 * Opens the native document scanner (iOS VisionKit / Android ML Kit via
 * @capgo/capacitor-document-scanner): live edge-detection, auto-capture when the
 * page is aligned, multi-page, perspective correction. Returns one record per
 * scanned page. Native-only (the plugin throws on web); the web path uses the
 * multi-select file input instead.
 *
 * Uses the plugin's `base64` response mode (not `imageFilePath`): the scanner
 * returns the image bytes directly, so there is no on-device file to read (the
 * iOS `imageFilePath` mode returns a `file://` URI that Capacitor's
 * Filesystem.readFile handles inconsistently) and no scanned temp file is
 * written to app storage — so `nativePath` is null and no post-upload sweep is
 * needed. (DEV-4252)
 *
 * @returns {Promise<Array<{ file: File, nativePath: null }>>}
 */
export const scanDocuments = async () => {
  try {
    const { scannedImages, status } = await DocumentScanner.scanDocument({
      responseType: 'base64',
      letUserAdjustCrop: true,
    });

    if (status !== 'success' || !scannedImages?.length) {
      return [];
    }

    return scannedImages.map(base64 => ({
      file: buildFileFromBlob(base64ToBlob(base64, DEFAULT_CAPTURE_MIME), 'jpeg'),
      nativePath: null,
    }));
  } catch (error) {
    logger.error('[NativeCamera] document scan failed', error);

    return [];
  }
};

const deleteIfPresent = async ({ path, directory }) => {
  try {
    await Filesystem.deleteFile(directory ? { path, directory } : { path });
  } catch (error) {
    logger.error('[NativeCamera] temp delete miss', { path, error });
  }
};

/**
 * Removes the on-device temp copies of a captured page after its upload is
 * confirmed, so document photos never linger in app storage.
 *
 * Sweeps both the path returned by the scanner plugin and the known Android
 * secondary copy under `Android/data/<pkg>/files/Pictures/`.
 *
 * DEVICE-TEST-PENDING: the Android secondary-copy location is the open cleanup
 * question in DEV-4252 — only verifiable on a real Android build; the Filesystem
 * plugin is a no-op on web and is never invoked in the web/Jest path. (DEV-4252)
 *
 * @param {string|null} nativePath path returned by {@link scanDocuments}
 */
export const deleteCapturedTempFile = async nativePath => {
  if (!nativePath) {
    return;
  }

  await deleteIfPresent({ path: nativePath });

  const fileName = nativePath.split('/').pop();

  if (fileName) {
    await deleteIfPresent({
      path: `${ANDROID_PICTURES_SUBDIR}/${fileName}`,
      directory: Directory.External,
    });
  }
};
